import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

const API_URL = '/cargo/api/cargo-requests';

export interface CreateCustomsProductRequest {
  nameOriginal: string;
  tnvedCodeOriginal?: string;
  productCost: number;
  currencyCode?: string;
  applicationScope?: string;
}

export interface UpdateCustomsProductRequest {
  nameForBroker?: string;
  tnvedCodeForBroker?: string;
  customsFees?: number;
  customsDuty?: number;
  vat?: number;
  nonTariffRegulationMeasures?: string;
}

// ─── Follow-up questions (шаг 2) ─────────────────────────────────────────────

export interface FollowUpOption {
  value: string;
  label: string;
  rateHint?: string;
}

export interface FollowUpQuestion {
  id: string;
  type: 'COUNTRY_SELECT' | 'SINGLE_SELECT' | 'QUANTITY_INPUT' | 'BOOLEAN';
  label: string;
  hint?: string;
  unit?: string;
  unitKey?: string;
  required: boolean;
  dependsOnQuestionId?: string;
  dependsOnValue?: string;
  options?: FollowUpOption[];
}

// ─── Certificate checklist ────────────────────────────────────────────────────

export interface RequiredDocument {
  id: number;
  name: string;
}

export interface CertificateChecklist {
  tnvedCode: string;
  tnvedName?: string;
  ntrNotFound?: boolean;
  aiGenerated?: boolean;
  /** Список реально требуемых документов — только нужные, ничего лишнего. */
  requiredDocuments: RequiredDocument[];
  notes?: string;
  /** Отдано из сохранённого ранее кэша, без нового обращения к AI. */
  fromCache?: boolean;
}

// ─── Refine request ───────────────────────────────────────────────────────────

export interface RefineCalculationRequest {
  countryOfOrigin?: string;
  exciseVariantValue?: string;
  quantities?: Record<string, number>;
  antiDumpingApplicable?: boolean;
  antiDumpingProducerValue?: string;
}

// ─── Product ──────────────────────────────────────────────────────────────────

export interface CustomsProduct {
  id: number;
  cargoRequestId: number;
  nameOriginal: string;
  tnvedCodeOriginal?: string;
  productCost: number;
  currencyCode?: string;
  productCostKzt?: number;
  exchangeRate?: number;
  applicationScope?: string;
  aiProductDescription?: string;
  nameForBroker?: string;
  tnvedCodeForBroker?: string;
  customsFees?: number;
  customsDutyPercent?: number;
  customsDuty?: number;
  vat?: number;
  nonTariffRegulationMeasures?: string;
  totalCustomsPayments?: number;
  tnvedCodeInDictionary?: boolean;

  // ─── Шаг 1: флаги точности ────────────────────────────────────────────────
  rateSource?: 'WTO' | 'ETT' | 'NONE';
  specificRateSkipped?: boolean;
  specificMinPresent?: boolean;
  exciseSkipped?: boolean;
  antiDumpingSkipped?: boolean;
  approximate?: boolean;

  // ─── Шаг 2: уточнённые значения ───────────────────────────────────────────
  excise?: number;
  antiDumpingDuty?: number;
  countryOfOrigin?: string;
  refined?: boolean;
  exciseVariantValue?: string;
  quantities?: Record<string, number>;
  antiDumpingApplicable?: boolean;
  antiDumpingProducerValue?: string;

  // ─── Алкоголь/табак — акциз не рассчитывается ─────────────────────────────
  restrictedGoodsCategory?: 'ALCOHOL' | 'TOBACCO' | null;
  exciseExcluded?: boolean;

  // ─── Динамические вопросы ─────────────────────────────────────────────────
  followUpQuestions?: FollowUpQuestion[];
}

@Injectable({
  providedIn: 'root'
})
export class CustomsCalculationService {
  constructor(private http: HttpClient) {}

  createProduct(cargoRequestId: number, request: CreateCustomsProductRequest): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(`${API_URL}/${cargoRequestId}/customs-products`, request);
  }

  getProducts(cargoRequestId: number): Observable<CustomsProduct[]> {
    return this.http.get<CustomsProduct[]>(`${API_URL}/${cargoRequestId}/customs-products`);
  }

  updateProduct(cargoRequestId: number, productId: number, request: UpdateCustomsProductRequest): Observable<CustomsProduct> {
    return this.http.put<CustomsProduct>(`${API_URL}/${cargoRequestId}/customs-products/${productId}`, request);
  }

  updateProductByOwner(cargoRequestId: number, productId: number, request: CreateCustomsProductRequest): Observable<CustomsProduct> {
    return this.http.put<CustomsProduct>(`${API_URL}/${cargoRequestId}/customs-products/${productId}/owner`, request);
  }

  deleteProduct(cargoRequestId: number, productId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/${cargoRequestId}/customs-products/${productId}`);
  }

  startCalculation(cargoRequestId: number): Observable<void> {
    return this.http.post<void>(`${API_URL}/${cargoRequestId}/customs-products/start`, {});
  }

  completeCalculation(cargoRequestId: number): Observable<void> {
    return this.http.post<void>(`${API_URL}/${cargoRequestId}/customs-products/complete`, {});
  }

  calculateWithAi(cargoRequestId: number, productId: number, applicationScope: string): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(
      `${API_URL}/${cargoRequestId}/customs-products/${productId}/calculate-with-ai`,
      { applicationScope }
    );
  }
}
